/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
/**
 * SendEngagementLetterDialog (Intake)
 * ----------------------------------
 * Intake-specific dialog for sending the Engagement Letter (LOE).
 * Follows the same UI as CreateLetter but orchestrates the intake flow:
 *  1. Fetch template & fields (no matterId).
 *  2. Call sendLoe mutation (creates/reuses Matter + DocumentInstance).
 *  3. Immediately POST /send/ to dispatch for signatures.
 */

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { PDFPreview } from '@/components/base/PDFPreview';
import { LetterFormContent } from '@/components/letters/LetterFormShared';
import { useSubjectDocumentsSendMutation } from '@/services/api/documentSubjectService';
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateFieldsQuery,
  useTemplateListQuery,
} from '@/services/api/generated/templatesV2Service';
import {
  useSendLoeMutation,
  useSendNewLoeMutation,
  useUpdateIntakeMutation,
} from '@/services/api/intakeService';
import { IntakeFormData as IntakeFormDataBackend } from '@/services/types/intake-types';
import {
  adaptTemplateFields,
  DocumentFieldMapping,
  DocumentTemplate,
} from '@/services/types/letters-types';

import { DocxHtmlPreview } from './DocxHtmlPreview';
import { TemplatePreviewErrorBoundary } from './TemplatePreviewErrorBoundary';
import { TemplateSelectionCard } from './TemplateSelectionCard';
import { useTemplatePreview } from './useTemplatePreview';
import { mapMergeFieldsToIntakePatch } from './utils/mergeFieldToIntakePatch';

interface Props {
  intakeId: string;
  firmId: string;
  open: boolean;
  onClose: () => void;
  onSent?: (intakeId: string, documentId: string) => void;
  /**
   * Pre-filled merge-field values sourced from the live Intake form (client-side).
   * These values override the template defaults but can still be edited by the user.
   */
  initialMergeFields?: Record<string, string>;
  prevFormData: IntakeFormDataBackend;
  /**
   * When true, uses the send-loe-new endpoint and requests backend to cancel
   * the latest cancellable LOE before creating this one.
   */
  cancelPrevious?: boolean; // defaults to true
}

const StyledDialogTitle = styled(DialogTitle)(() => ({
  fontSize: 36,
  padding: 0,
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  padding: 0,
}));

const StyledDialogActions = styled(DialogActions)(() => ({
  padding: 0,
  display: 'flex',
  gap: 8,
  '& .MuiButton-contained, & .MuiButton-outlined': {
    flex: 1,
  },
}));

// Template Preview Dialog Component
const TemplatePreviewDialog = React.memo(
  ({
    template,
    firmId,
    fields,
    open,
    onClose,
  }: {
    template: DocumentTemplate | null;
    firmId: string;
    fields?: Record<string, string | null>;
    open: boolean;
    onClose: () => void;
  }) => {
    const { previewBlob, previewHtml, isLoading, error, previewType } =
      useTemplatePreview(template, firmId, undefined, fields);

    if (!template) return null;

    const isExternal = template.format === 'EXTERNAL';

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="h6">Preview: {template.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {template.requires_signatures
                ? 'Requires signatures'
                : 'No signatures required'}
            </Typography>
          </Box>
          {!isExternal && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', color: 'error.main' }}
              >
                Preview formatting may not be accurate - use for text verification
                only.
              </Typography>
            </Alert>
          )}
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'hidden', p: 0 }}>
          {isExternal ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Stack spacing={2} alignItems="center">
                <Typography variant="body1" color="text.secondary">
                  Preview not available for Adobe Sign templates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This template will be sent via Adobe Sign for signatures.
                </Typography>
              </Stack>
            </Box>
          ) : (
          <TemplatePreviewErrorBoundary>
            {isLoading && !previewHtml && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography>
                  {previewType === 'html'
                    ? 'Generating fast preview...'
                    : 'Loading preview...'}
                </Typography>
              </Box>
            )}
            {error && (
              <Box sx={{ p: 3 }}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    Unable to generate a full preview at this time. The template
                    will be populated with your intake data when you proceed.
                  </Typography>
                </Alert>
                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Typography variant="body1">
                    <strong>Template Name:</strong> {template.name}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Type:</strong> Engagement Letter
                  </Typography>
                  <Typography variant="body1">
                    <strong>Requires Signatures:</strong>{' '}
                    {template.requires_signatures ? 'Yes' : 'No'}
                  </Typography>
                </Stack>
              </Box>
            )}
            {!error && (previewHtml || previewBlob) && (
              <>
                {previewType === 'html' && previewHtml && (
                  <DocxHtmlPreview
                    htmlContent={previewHtml}
                    width="100%"
                    height="100%"
                  />
                )}
                {previewType === 'pdf' && previewBlob && (
                  <PDFPreview blob={previewBlob} width="100%" height="100%" />
                )}
                {/* Show both if fast preview is showing HTML but PDF is loading in background */}
                {previewType === 'html' && previewBlob && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                    }}
                  >
                    Fast Preview • PDF ready for signatures
                  </Box>
                )}
              </>
            )}
          </TemplatePreviewErrorBoundary>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

TemplatePreviewDialog.displayName = 'TemplatePreviewDialog';

export const SendEngagementLetterDialog = ({
  intakeId,
  firmId,
  open,
  onClose,
  onSent,
  initialMergeFields = {},
  prevFormData,
  cancelPrevious = true,
}: Props) => {
  /* ------------------------------------------------------------------ */
  /* State for template selection                                      */
  /* ------------------------------------------------------------------ */
  const [selectedTemplate, setSelectedTemplate] =
    useState<DocumentTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] =
    useState<DocumentTemplate | null>(null);
  const [showForm, setShowForm] = useState(false);

  /* ------------------------------------------------------------------ */
  /* Fetch engagement-letter templates & fields                         */
  /* ------------------------------------------------------------------ */
  const { data: firmTemplatesData, isLoading: isLoadingFirmTemplates } =
    useTemplateListQuery(
      firmId
        ? { firmId, scope: 'FIRM', status: 'ACTIVE', category: 'RETAINER' }
        : (skipToken as unknown as { firmId: string; scope: 'FIRM'; status: 'ACTIVE'; category: 'RETAINER' }),
    );
  const { data: globalTemplatesData, isLoading: isLoadingGlobalTemplates } =
    useTemplateListQuery({
      scope: 'GLOBAL',
      status: 'ACTIVE',
      category: 'RETAINER',
    });

  const firmTemplates = (firmTemplatesData?.results ?? []) as unknown as DocumentTemplate[];
  const globalTemplates = (globalTemplatesData?.results ?? []) as unknown as DocumentTemplate[];

  // Prefer firm RETAINER templates; fallback to GLOBAL RETAINER templates.
  const engagementLetterTemplates = useMemo(() => {
    const firmRetainers = firmTemplates.filter((t) => t.category === 'RETAINER');
    if (firmRetainers.length > 0) {
      return firmRetainers;
    }
    return globalTemplates.filter((t) => t.category === 'RETAINER');
  }, [firmTemplates, globalTemplates]);

  // Auto-select template and show form if there's only one template.
  // Wait for firm templates to finish loading so the global fallback is
  // intentional, not a race-condition artefact (ENG-336).
  useEffect(() => {
    if (isLoadingFirmTemplates) return;
    if (engagementLetterTemplates.length === 1 && !selectedTemplate) {
      setSelectedTemplate(engagementLetterTemplates[0]);
      setShowForm(true);
    }
  }, [engagementLetterTemplates, isLoadingFirmTemplates]);

  // Determine if we should show template selection
  const showTemplateSelection = !showForm;

  const handleContinueToForm = useCallback(() => {
    setShowForm(true);
  }, []);

  const handleBackToSelection = useCallback(() => {
    setShowForm(false);
  }, []);

  const handleTemplateSelect = useCallback((template: DocumentTemplate) => {
    setSelectedTemplate(template);
  }, []);

  const handleTemplatePreview = useCallback((template: DocumentTemplate) => {
    setPreviewTemplate(template);
  }, []);

  const { data: rawTemplateFields, isLoading: isLoadingFields } =
    useTemplateFieldsQuery(
      selectedTemplate
        ? { id: selectedTemplate.id }
        : (skipToken as unknown as { id: string }),
    );
  // Map v2 ReadTemplateField[] into bridge DocumentTemplateField shape for LetterFormContent
  const templateFields = useMemo(() => {
    if (!rawTemplateFields) return undefined;
    const v2Fields = rawTemplateFields as unknown as ReadTemplateField[];
    return { fields: adaptTemplateFields(v2Fields) };
  }, [rawTemplateFields]);

  // Convert initialMergeFields (reference-keyed) to context_key-keyed for render endpoints
  const previewFields = useMemo(() => {
    if (!rawTemplateFields || !initialMergeFields) return undefined;
    const v2Fields = rawTemplateFields as unknown as ReadTemplateField[];
    const refToContextKey: Record<string, string> = {};
    v2Fields.forEach((f) => {
      if (f.reference && f.context_key) {
        refToContextKey[f.reference] = f.context_key;
      }
    });
    const mapped: Record<string, string | null> = {};
    Object.entries(initialMergeFields).forEach(([ref, value]) => {
      const contextKey = refToContextKey[ref];
      if (contextKey) {
        mapped[contextKey] = value;
      }
    });
    return Object.keys(mapped).length > 0 ? mapped : undefined;
  }, [rawTemplateFields, initialMergeFields]);

  /* ------------------------------------------------------------------ */
  /* Mutations                                                          */
  /* ------------------------------------------------------------------ */
  const [sendLoe, { isLoading: isSendingExisting }] = useSendLoeMutation();
  const [sendNewLoe, { isLoading: isSendingNew }] = useSendNewLoeMutation();
  const [sendDoc, { isLoading: isDispatching }] =
    useSubjectDocumentsSendMutation();
  const [updateIntake] = useUpdateIntakeMutation();
  const [progressText, setProgressText] = useState<string | null>(null);

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (mergeFields: Record<string, string>) => {
    if (!selectedTemplate) {
      toast.error('Please select an engagement letter template');
      return;
    }
    try {
      // Simple and concise: client-side prefill → user edits. The backend
      // will silently ignore keys it doesn't recognise, but *will* validate
      // required ones like `client_email_address`, which is already part of
      // `initialMergeFields` when present.
      const finalMergeFields = {
        ...initialMergeFields,
        ...mergeFields,
      } as Record<string, string>;

      // Opt-OUT flag: keep phone number by default everywhere. Developers can
      // disable phone-auth locally by adding VITE_DISABLE_ADOBE_PHONE_AUTH=true
      // to their .env or shell environment.
      const phoneAuthDisabled =
        import.meta.env.VITE_DISABLE_ADOBE_PHONE_AUTH === 'true';
      if (phoneAuthDisabled) {
        delete finalMergeFields.client_phone_number;
      }

      const res = cancelPrevious
        ? await sendNewLoe({
            intakeId,
            data: {
              document_template_id: selectedTemplate.id,
              merge_fields: finalMergeFields,
              cancel_previous: true,
            },
          }).unwrap()
        : await sendLoe({
            intakeId,
            data: {
              document_template_id: selectedTemplate.id,
              merge_fields: finalMergeFields,
            },
          }).unwrap();

      // Backend now handles sending automatically, no separate send call needed
      await sendDoc({
        subjectType: 'intake',
        subjectId: intakeId,
        id: res.document.id,
        // Backend requires body but doesn't use it for send action
        documentInstance: {} as Parameters<typeof sendDoc>[0]['documentInstance'],
      }).unwrap();
      onSent?.(intakeId, res.document.id);
      toast.success(
        cancelPrevious
          ? 'Replaced LOE and sent new letter'
          : 'Engagement Letter sent',
      );
      onClose();
    } catch (err) {
      console.error(err);

      let errorMessage = cancelPrevious
        ? 'Failed to create and send new LOE'
        : 'Failed to send Engagement Letter';
      if (err && typeof err === 'object' && 'data' in err) {
        const errorData = (err as any).data;
        if (errorData?.error) {
          errorMessage = `Failed to send Engagement Letter - ${errorData.error}`;
        }
      }
      toast.error(errorMessage);
    }
  };

  // Always include email address in the fields to render (memoized)
  const visibleFields = useMemo(() => {
    let fields = templateFields?.fields || [];
    const emailFieldExists = fields.some(
      (f) => f.mapping === 'client_email_address',
    );

    if (!emailFieldExists && selectedTemplate) {
      fields = [
        ...fields,
        {
          id: 'client_email_address',
          display_name: 'Email Address',
          default_value: '',
          // V2 names
          reference: 'client_email_address',
          field_type: 'TEXT',
          required: true,
          template_id: selectedTemplate.id,
          context_key: 'client_email_address',
          // Legacy aliases
          mapping: DocumentFieldMapping.CLIENT_EMAIL_ADDRESS,
          external_reference: 'client_email_address',
          type: 'TEXT',
          is_required: true,
          document_template: selectedTemplate.id,
        },
      ];
    }

    return fields;
  }, [templateFields?.fields, selectedTemplate]);

  // All visible fields are required (memoized)
  const requiredMappings = useMemo(
    () => visibleFields.map((f) => f.mapping),
    [visibleFields],
  );

  const isLoading = useMemo(
    () =>
      isLoadingFirmTemplates ||
      isLoadingGlobalTemplates ||
      isLoadingFields ||
      isSendingExisting ||
      isSendingNew ||
      isDispatching,
    [
      isLoadingFirmTemplates,
      isLoadingGlobalTemplates,
      isLoadingFields,
      isSendingExisting,
      isSendingNew,
      isDispatching,
    ],
  );

  // Custom submit handler to validate all fields before network request
  const handleFormSubmit = useCallback(
    async (data: Record<string, string>) => {
      // Check if any visible field is empty
      const missingField = visibleFields.find(
        (field) =>
          !data[field.external_reference] ||
          data[field.external_reference].trim() === '',
      );
      if (missingField) {
        setValidationError('All fields must be filled out.');
        return;
      }
      setValidationError(null);

      // PATCH intake before sending document
      setProgressText('Updating intake...');
      try {
        await updateIntake({
          id: intakeId,
          skipOptimistic: true,
          form_data: mapMergeFieldsToIntakePatch(data, prevFormData),
        }).unwrap();
      } catch (err) {
        setProgressText(null);
        toast.error('Failed to update intake. Please try again.');
        return;
      }

      setProgressText('Creating document...');
      try {
        await handleSubmit(data);
      } finally {
        setProgressText(null);
      }
    },
    [visibleFields, updateIntake, intakeId, prevFormData, handleSubmit],
  );

  return (
    <>
      <TemplatePreviewDialog
        template={previewTemplate}
        firmId={firmId}
        fields={previewFields}
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
      <Dialog
        open={open}
        onClose={isLoading ? undefined : onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              minWidth: 680,
              maxWidth: 750,
              p: 3,
              borderRadius: 4,
              overflowX: 'hidden',
            },
          },
        }}
      >
        <Stack spacing={3} sx={{ overflowX: 'hidden', width: '100%' }}>
          <StyledDialogTitle>
            <Typography variant="h1">
              {cancelPrevious ? 'Create New LOE' : 'Send Engagement Letter'}
            </Typography>
            {selectedTemplate && !showTemplateSelection && (
              <Typography variant="subtitle1">
                {selectedTemplate.name}
              </Typography>
            )}
          </StyledDialogTitle>

          <StyledDialogContent>
            {showTemplateSelection ? (
              // Template Selection View
              engagementLetterTemplates.length > 0 ? (
                <Stack spacing={2}>
                  <Typography variant="body1">
                    Please select an engagement letter template:
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold', color: 'error.main' }}
                    >
                      Note: Previews are not formatted correctly and are only
                      for text verification. You may see certain text elements
                      (%, brackets, etc) that will not be visible when the
                      letter is sent.
                    </Typography>
                  </Alert>
                  <TemplatePreviewErrorBoundary>
                    <div className="space-y-3 p-1">
                      {engagementLetterTemplates.map((template) => (
                        <TemplateSelectionCard
                          key={template.id}
                          template={template}
                          isSelected={selectedTemplate?.id === template.id}
                          onSelect={handleTemplateSelect}
                          onPreview={handleTemplatePreview}
                        />
                      ))}
                    </div>
                  </TemplatePreviewErrorBoundary>
                </Stack>
              ) : (
                <Alert severity="info" sx={{ m: 3 }}>
                  No engagement letter templates were found for this firm or as
                  global defaults. Please contact your administrator to add one.
                </Alert>
              )
            ) : // Form Fields View
            selectedTemplate ? (
              <LetterFormContent
                isLoading={isLoadingFields}
                templateFields={{ fields: visibleFields }}
                onSubmit={handleFormSubmit}
                requiredMappings={requiredMappings}
                initialValues={initialMergeFields}
              />
            ) : null}
            {validationError && !showTemplateSelection && (
              <Alert severity="error" sx={{ m: 3 }}>
                {validationError}
              </Alert>
            )}
          </StyledDialogContent>

          <StyledDialogActions>
            {showTemplateSelection ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onClose}
                  disabled={isLoading}
                  sx={{ height: 48, flex: 1 }}
                >
                  Cancel
                </Button>
                {selectedTemplate && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleContinueToForm}
                    sx={{ height: 48, flex: 1 }}
                  >
                    Continue
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBackToSelection}
                  disabled={isLoading}
                  sx={{ height: 48, flex: 1 }}
                >
                  Back
                </Button>
                {selectedTemplate && (
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    form="letter-form"
                    disabled={isLoading || !!progressText}
                    sx={{ height: 48, flex: 1 }}
                  >
                    {progressText ||
                      (isDispatching || isSendingExisting || isSendingNew
                        ? 'Sending...'
                        : cancelPrevious
                          ? 'Create + Send'
                          : 'Send Letter')}
                  </Button>
                )}
              </>
            )}
          </StyledDialogActions>
        </Stack>
      </Dialog>
    </>
  );
};
